import { AngularFirestore } from "@angular/fire/compat/firestore";
import { Injectable } from "@angular/core";
import { combineLatest, from, Observable, of } from "rxjs";
import { Impostazioni } from "../shared/interface.service";
import { Subject } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, filter, first, map, mergeMap, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { GlobalService } from "../shared/global.service";
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import {
  collection as modCollection,
  doc as modDoc,
  getCountFromServer,
  getFirestore as modGetFirestore,
} from 'firebase/firestore';

@Injectable({
  providedIn: "root",
})
export class FireService {



  // ######################################
  // FUNCTION: constructor
  constructor(public db: AngularFirestore) { }

  ngUnsubscribe: Subject<void> = new Subject<void>();

  // ######################################
  // FUNCTION: add
  add(collection: string, data: any, id: string): any {
    return this.db.collection(collection).doc(id).set(Object.assign({}, data));
  }

  // ######################################
  // FUNCTION: addCollection
  addCollection(collection1: string, collection2: string, data: any, id: string, id2: string): any {
    return this.db.collection(collection1).doc(id).collection(collection2).doc(id2).set(Object.assign({}, data));
  }



  async incrementUtilizzate(customLink: { clienti: string, licenze: string }, increment: number = 1): Promise<void> {
    const docRef = this.db.firestore
      .collection('clienti').doc(customLink.clienti)
      .collection('licenze').doc(customLink.licenze);

    try {
      // Firestore Transaction: lettura + scrittura atomiche, nessuna race condition
      await this.db.firestore.runTransaction(async (transaction) => {
        const snap = await transaction.get(docRef);
        if (!snap.exists) throw new Error('Documento licenza non trovato');
        const data = snap.data() as { utilizzate: number; totali: number };
        if (data.utilizzate + increment > data.totali) {
          throw new Error('Limite licenze raggiunto');
        }
        transaction.update(docRef, {
          utilizzate: firebase.firestore.FieldValue.increment(increment)
        });
      });
    } catch (error) {
      console.error("Errore nell'aggiornamento di Firebase:", error);
    }
  }


  // ######################################
  // FUNCTION: addSubCollection
  addSubCollection(
    collection1: string,
    collection2: string,
    collection3: string,
    data: any,
    id: string,
    id2: string,
    id3: string
  ): Promise<any> {
    return this.db
      .collection(collection1)
      .doc(id)
      .collection(collection2)
      .doc(id2)
      .collection(collection3)
      .doc(id3)
      .set(Object.assign({}, data));
  }

  // ######################################
  //  PUBLIC LINK HELPERS (collection `public-link`)
  // ######################################

  /** Legge il doc `public-link/{qv}` (live). */
  getPublicLink(qv: string): Observable<any> {
    return this.db
      .collection("public-link")
      .doc(qv)
      .valueChanges({ idField: "id" })
      .pipe(takeUntil(this.ngUnsubscribe));
  }

  /** Legge una sola volta il doc `public-link/{qv}`. */
  getPublicLinkOnce(qv: string): Promise<any> {
    return this.db
      .firestore.collection("public-link")
      .doc(qv)
      .get()
      .then((snap) => (snap.exists ? { id: snap.id, ...snap.data() } : null));
  }

  /** Crea/aggiorna `public-link/{qv}` con merge. */
  setPublicLink(qv: string, data: any, merge: boolean = true): Promise<void> {
    return this.db.collection("public-link").doc(qv).set(Object.assign({}, data), { merge });
  }

  /** Attiva/disattiva il link senza toccare gli altri campi. */
  togglePublicLink(qv: string, attivo: boolean): Promise<void> {
    return this.db.collection("public-link").doc(qv).set({ attivo }, { merge: true });
  }

  /** One-shot anni con count (no live listener). */
  async getPublicYearsOnce(qv: string): Promise<any[]> {
    try {
      const snap = await this.db.firestore
        .collection("public-link")
        .doc(qv)
        .collection("anni")
        .get();
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.debug("getPublicYearsOnce error:", err);
      return [];
    }
  }

  /** Carica una "pagina" di compilazioni di un anno.
   *  `startAfter` (numero offset client-side) consente paginazione semplice
   *  senza dipendere da `orderBy` server-side (che in alcune install
   *  Firestore non ritorna i doc). Il primo `.get()` scarica TUTTI i doc
   *  dell'anno: per anni con >>10k doc è preferibile paginare server-side
   *  con un campo indicizzato su uno schema differente. */
  async getPublicCompilazioniPage(
    qv: string,
    anno: string,
    pageSize: number = 50,
    startAfter?: any
  ): Promise<{ items: any[]; lastDoc: any | null; hasMore: boolean }> {
    try {
      const snap = await this.db.firestore
        .collection("public-link")
        .doc(qv)
        .collection("anni")
        .doc(anno)
        .collection("compilazioni")
        .get();
      const all = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
      const offset = typeof startAfter === "number" ? startAfter : 0;
      const slice = all.slice(offset, offset + pageSize);
      const nextOffset = offset + slice.length;
      return {
        items: slice,
        lastDoc: nextOffset,
        hasMore: nextOffset < all.length,
      };
    } catch (err) {
      console.debug("getPublicCompilazioniPage error:", err);
      return { items: [], lastDoc: null, hasMore: false };
    }
  }

  /** Risolve uno slug pubblico in qvId. Cerca `public-link` dove slug == X. */
  async resolvePublicSlug(slug: string): Promise<string> {
    if (!slug) return "";
    try {
      const snap = await this.db.firestore
        .collection("public-link")
        .where("slug", "==", slug)
        .limit(1)
        .get();
      if (snap.empty) return "";
      return snap.docs[0].id;
    } catch (err) {
      console.debug("resolvePublicSlug error:", err);
      return "";
    }
  }

  /** Lista degli anni (subcollection `public-link/{qv}/anni`). */
  getPublicYears(qv: string): Observable<any[]> {
    return this.db
      .collection("public-link")
      .doc(qv)
      .collection("anni")
      .valueChanges({ idField: "id" })
      .pipe(takeUntil(this.ngUnsubscribe));
  }

  /** Lista compilazioni di un anno. */
  getPublicCompilazioni(qv: string, anno: string): Observable<any[]> {
    return this.db
      .collection("public-link")
      .doc(qv)
      .collection("anni")
      .doc(anno)
      .collection("compilazioni")
      .valueChanges({ idField: "id" })
      .pipe(takeUntil(this.ngUnsubscribe));
  }

  /** Singola compilazione. */
  getPublicCompilazione(qv: string, anno: string, sid: string): Observable<any> {
    return this.db
      .collection("public-link")
      .doc(qv)
      .collection("anni")
      .doc(anno)
      .collection("compilazioni")
      .doc(sid)
      .valueChanges({ idField: "id" })
      .pipe(takeUntil(this.ngUnsubscribe));
  }

  /** Scrive la compilazione su `public-link/{qv}/anni/{anno}/compilazioni/{sid}` con merge. */
  savePublicCompilazione(qv: string, anno: string, sid: string, data: any): Promise<void> {
    return this.db
      .collection("public-link")
      .doc(qv)
      .collection("anni")
      .doc(anno)
      .collection("compilazioni")
      .doc(sid)
      .set(Object.assign({}, data), { merge: true });
  }

  /** Rimuove una compilazione dall'anno. */
  deletePublicCompilazione(qv: string, anno: string, sid: string): Promise<void> {
    return this.db
      .collection("public-link")
      .doc(qv)
      .collection("anni")
      .doc(anno)
      .collection("compilazioni")
      .doc(sid)
      .delete();
  }

  /** Upsert del doc `anni/{anno}` con il count corrente (merge). */
  async setPublicYearCount(qv: string, anno: string, count: number): Promise<void> {
    try {
      await this.db
        .collection("public-link")
        .doc(qv)
        .collection("anni")
        .doc(anno)
        .set({ count }, { merge: true });
    } catch (err) {
      console.debug("setPublicYearCount error:", err);
    }
  }

  /**
   * Conteggio compilazioni di un anno.
   * Strategia primaria: `.get()` sulla subcollection (1 read per doc).
   * Per collezioni molto grandi (>10k) questa chiamata è costosa —
   * usare dal bottone manuale di sync. In futuro, quando le rules permettono
   * `list` sull'aggregate, si può switchare a `getCountFromServer`.
   */
  async countPublicCompilazioni(qv: string, anno: string): Promise<number> {
    // Primary: full .get() — stesse rules dei single-doc read.
    try {
      const full = await this.db.firestore
        .collection("public-link")
        .doc(qv)
        .collection("anni")
        .doc(anno)
        .collection("compilazioni")
        .get();
      return full.size || 0;
    } catch {}

    // Fallback: count aggregation compat se esposto
    try {
      const q: any = this.db.firestore
        .collection("public-link")
        .doc(qv)
        .collection("anni")
        .doc(anno)
        .collection("compilazioni");
      if (q && typeof q.count === "function") {
        const snap = await q.count().get();
        return Number(snap?.data?.()?.count ?? 0);
      }
    } catch {}

    // Fallback: modular count aggregation
    try {
      const fs = modGetFirestore(firebase.app() as any);
      const ref = modCollection(
        modDoc(fs, "public-link", qv),
        "anni",
        anno,
        "compilazioni"
      );
      const snap = await getCountFromServer(ref);
      return Number(snap.data().count || 0);
    } catch {}

    return 0;
  }

  /**
   * Conteggio compilazioni di una licenza cliente.
   * Strategia primaria: count aggregation (≈1 read, nessun download dei doc).
   * Fallback: `.get()` completo se l'aggregate non è disponibile/permesso.
   */
  async countLicenzaCompilazioni(azienda: string, licenza: string): Promise<number> {
    // Primary: count aggregation compat se esposto.
    try {
      const q: any = this.db.firestore
        .collection("clienti")
        .doc(azienda)
        .collection("licenze")
        .doc(licenza)
        .collection("compilazioni");
      if (q && typeof q.count === "function") {
        const snap = await q.count().get();
        return Number(snap?.data?.()?.count ?? 0);
      }
    } catch {}

    // Fallback 1: count aggregation modulare.
    try {
      const fs = modGetFirestore(firebase.app() as any);
      const ref = modCollection(
        modDoc(fs, "clienti", azienda),
        "licenze",
        licenza,
        "compilazioni"
      );
      const snap = await getCountFromServer(ref);
      return Number(snap.data().count || 0);
    } catch {}

    // Fallback 2: full .get() — scarica tutti i doc (costoso ma sempre valido).
    try {
      const full = await this.db.firestore
        .collection("clienti")
        .doc(azienda)
        .collection("licenze")
        .doc(licenza)
        .collection("compilazioni")
        .get();
      return full.size || 0;
    } catch {}

    return 0;
  }

  /**
   * Conteggio generico dei doc di una sub-sub-collection (clienti/{id}/coll2/{id2}/coll3).
   * Strategia: count aggregation (~1 read) → fallback modulare → fallback `.get()` completo.
   * Usare al posto di scaricare l'intera collezione solo per `.length`.
   */
  async countSubCollection(
    collection: string,
    id: string,
    collection2: string,
    id2: string,
    collection3: string
  ): Promise<number> {
    // Primary: count aggregation compat.
    try {
      const q: any = this.db.firestore
        .collection(collection)
        .doc(id)
        .collection(collection2)
        .doc(id2)
        .collection(collection3);
      if (q && typeof q.count === "function") {
        const snap = await q.count().get();
        return Number(snap?.data?.()?.count ?? 0);
      }
    } catch {}

    // Fallback 1: count aggregation modulare.
    try {
      const fs = modGetFirestore(firebase.app() as any);
      const ref = modCollection(modDoc(fs, collection, id), collection2, id2, collection3);
      const snap = await getCountFromServer(ref);
      return Number(snap.data().count || 0);
    } catch {}

    // Fallback 2: full .get() (costoso ma sempre valido).
    try {
      const full = await this.db.firestore
        .collection(collection)
        .doc(id)
        .collection(collection2)
        .doc(id2)
        .collection(collection3)
        .get();
      return full.size || 0;
    } catch {}

    return 0;
  }

  /** Incremento atomico di `anni/{anno}.count` (+1). Crea il doc anno se non esiste. */
  async incrementPublicYearCount(qv: string, anno: string): Promise<void> {
    const ref = this.db.firestore
      .collection("public-link")
      .doc(qv)
      .collection("anni")
      .doc(anno);
    try {
      await this.db.firestore.runTransaction(async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists) {
          tx.set(ref, { count: 1, creato: firebase.firestore.FieldValue.serverTimestamp() });
        } else {
          tx.update(ref, { count: firebase.firestore.FieldValue.increment(1) });
        }
      });
    } catch (err) {
      console.debug("incrementPublicYearCount error:", err);
    }
  }

  // ######################################
  // FUNCTION: get
  get(collection: string, id: string): Observable<any> {
    return this.db.collection(collection).doc(id).valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
  }

  async copyDocument(sourcePath: string, targetPath: string): Promise<void> {
    try {
      // Get the source document
      const sourceDocRef = this.db.doc(sourcePath).ref;
      const sourceDoc = await sourceDocRef.get();

      if (sourceDoc.exists) {
        // Get the data from the source document
        const data = sourceDoc.data();

        if (data) {
          // Write the data to the target document (this will create it if it doesn't exist)
          const targetDocRef = this.db.doc(targetPath).ref;
          await targetDocRef.set(data);

          console.log('Document copied successfully!');
        }
      } else {
        console.error('Source document does not exist.');
      }
    } catch (error) {
      console.error('Error copying document: ', error);
    }
  }


  // ######################################
  // FUNCTION: getCollection
  getCollection(collection: string, id: string, collection2: string): Observable<any> {
    return this.db.collection(collection).doc(id).collection(collection2).valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
  }


  checkPassword(user: string, clienti: string, reparto: string, pwd: string): Promise<boolean> {
    // Usa `getSubDocOnce` (basato su `.get()`) invece di `valueChanges` per
    // evitare la race sul client-cache di Firestore: dopo una `update()`
    // appena risolta, `valueChanges` può emettere il doc cached pre-update
    // prima del refresh. `.get()` rispetta la read-after-write consistency.
    return new Promise((resolve, reject) => {
      this
        .getSubDocOnce('clienti', clienti, 'reparti', reparto, 'utenti', user)
        .pipe(
          take(1),
          map((snap: any) => {
            const doc = snap?.exists ? snap.data() : null;
            if (!doc) {
              console.warn('[checkPassword] doc non trovato', { path: `clienti/${clienti}/reparti/${reparto}/utenti/${user}` });
              return false;
            }
            const match = this.checkPasswordMatch(pwd, doc.pwd);
            if (!match) {
              console.warn('[checkPassword] pwd mismatch', {
                path: `clienti/${clienti}/reparti/${reparto}/utenti/${user}`,
                attivo: doc.attivo,
                admin: doc.admin,
              });
            }
            return match;
          }),
          catchError((error) => {
            console.error('[checkPassword] Firestore error:', error);
            return of(false);
          })
        )
        .subscribe({
          next: (isPasswordValid) => resolve(isPasswordValid),
          error: (err) => reject(err),
        });
    });
  }

  private checkPasswordMatch(enteredPwd: string, storedPwd: string): boolean {
    return enteredPwd === storedPwd;
  }


  getCliente(data): Observable<any> {
    const newPassword = this.generatePassword();
    const target = data.user.trim().toLowerCase();

    // FAST PATH: collectionGroup indicizzato su `email` → 1 lettura invece di
    // scansionare TUTTI i clienti + le subcollection reparti/R1/utenti (N+1).
    // Richiede l'indice collectionGroup 'utenti'(email) DEPLOYATO
    // (vedi firestore.indexes.json). Fallback alla scansione completa quando:
    //  - l'indice manca (la query lancia → catchError),
    //  - l'email salvata non è normalizzata e l'==​ case-sensitive non combacia
    //    (alcuni path la salvano raw) → nessun match → scansione completa.
    // collectionGroup('utenti') intercetta anche gli admin top-level utenti/{id}:
    // il filtro sul path tiene solo gli utenti cliente sotto reparto R1.
    const fast$ = from(
      this.db.firestore.collectionGroup('utenti').where('email', '==', target).get()
    ).pipe(
      map((snap: any) =>
        snap.docs
          .filter((d: any) => /^clienti\/[^/]+\/reparti\/R1\/utenti\/[^/]+$/.test(d.ref.path))
          .map((d: any) => ({ id: d.id, azienda: d.ref.parent.parent.parent.parent.id, ...d.data() }))
          .find((c: any) => c?.admin)
      ),
      catchError(() => of(undefined))
    );

    return fast$.pipe(
      concatMap((match: any) => {
        if (match) {
          // TTL magic-link: 24h. Default 10 min era troppo corto per l'apertura email.
          let formatData = { 'pwd': newPassword, 'deadline': GlobalService.getDeadline(24 * 60), 'azienda': match.azienda, 'reparti': 'R1', 'utenti': match.id, 'descrizione': match.descrizione };
          return from(this.updateSubDoc('clienti', 'reparti', 'utenti', formatData, match.azienda, 'R1', match.id)).pipe(map(() => formatData));
        }
        return this.getClienteFullScan(data, newPassword);
      })
    );
  }

  // Scansione completa (legacy): letta come fallback dal fast path di getCliente.
  // Lettura one-time (no listener pendente): il flusso usa già take(1)/defaultIfEmpty.
  private getClienteFullScan(data, newPassword: string): Observable<any> {
    return from(this.db.firestore.collection('clienti').get()).pipe(
      map((snap: any) => snap.docs.map((d: any) => ({ id: d.id, ...d.data() }))),
      map((clienti: any[]) => clienti.sort((a, b) => Number(b['id']) - Number(a['id']))),
      concatMap((sortedClienti) => {
        return from(sortedClienti).pipe(
          mergeMap(client =>
            from(this.getSubCollection('clienti', client.id, 'reparti', 'R1', 'utenti')).pipe(
              first(),
              mergeMap((clients: any[]) => {
                const matchingClient = clients.find(
                  (c: any) => c?.email?.trim().toLowerCase() === data.user.trim().toLowerCase()
                );

                if (matchingClient && matchingClient.admin) {
                  // TTL magic-link: 24h. Default 10 min era troppo corto per l'apertura email.
                  let formatData = { 'pwd': newPassword, 'deadline': GlobalService.getDeadline(24 * 60), 'azienda': client.id, 'reparti': 'R1', 'utenti': matchingClient.id, 'descrizione': matchingClient.descrizione };
                  return from(this.updateSubDoc('clienti', 'reparti', 'utenti', formatData, client.id, 'R1', matchingClient.id)).pipe(map(() => formatData));
                }
                return of(null);
              })
            )
          ),
          filter(result => result !== null), // Filtra solo risultati validi
          take(1), // Prendi solo il primo risultato valido
          defaultIfEmpty({}) // Se nessun cliente corrisponde, restituisci {}
        );
      })
    );
  }


  // Function to generate a random password using CSPRNG
  generatePassword(): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const randomValues = new Uint32Array(12);
    crypto.getRandomValues(randomValues);
    return Array.from(randomValues)
      .map(v => characters[v % characters.length])
      .join('');
  }


  getClientiWithLicenze(): Observable<any[]> {
    return this.db.collection('clienti', ref =>
      ref.orderBy('createdAt', 'desc') // Ordina i clienti in ordine decrescente
    ).valueChanges({ idField: 'id' })
      .pipe(takeUntil(this.ngUnsubscribe));
  }

  getClientiSorted(): Observable<any> {
    return this.db.collection('clienti', ref =>
      ref.orderBy('createdAt', 'desc') // Ordina per createdAt in modo decrescente
    ).valueChanges({ idField: "id" })
      .pipe(takeUntil(this.ngUnsubscribe)); // Gestione della disiscrizione
  }

  // ######################################
  // FUNCTION: getSubCollection
  getSubCollection(
    collection: string,
    id: string,
    collection2: string,
    id2: string,
    collection3: string
  ): Observable<any> {
    return this.db
      .collection(collection)
      .doc(id)
      .collection(collection2)
      .doc(id2)
      .collection(collection3)
      .valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
  }
  // ######################################
  // FUNCTION: getSubCollection
  getSubCollectionOnce(
    collection: string,
    id: string,
    collection2: string,
    id2: string,
    collection3: string
  ): Observable<any> {
    return this.db.collection(collection).doc(id).collection(collection2).doc(id2).collection(collection3).get().pipe(takeUntil(this.ngUnsubscribe));
  }


  async getNextUtenteIndexByReparto(
    azienda: string,
    licenza: string,
    reparto: string // es: "R3"
  ): Promise<number> {
    const prefix = `${reparto}U`;
    const end = prefix + '\uf8ff';

    const ref = this.db.firestore
      .collection('clienti')
      .doc(azienda)
      .collection('licenze')
      .doc(licenza)
      .collection('compilazioni');

    // ✅ query per prefisso sul documentId (__name__)
    const snap = await ref
      .orderBy('__name__')
      .startAt(prefix)
      .endAt(end)
      .get();

    // ✅ calcolo MAX numerico su U
    let maxU = 0;

    for (const d of snap.docs) {
      const id = d.id; // es: "R3U15"
      const m = id.match(/U(\d+)$/i);
      if (!m) continue;
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > maxU) maxU = n;
    }

    return maxU + 1; // prossimo utente
  }

  /**
   * Riserva ATOMICAMENTE il prossimo indice utente per un reparto.
   * Due scansioni QR concorrenti NON ottengono mai lo stesso indice:
   * niente collisione docId (compilazione persa) né slot sforato.
   *
   * Usa un contatore in transaction (`licenze/{lic}/contatori/{reparto}`):
   * il contatore è letto dentro la transaction, quindi una scrittura
   * concorrente forza il retry e fa vedere il valore aggiornato.
   * Seed lazy dal max esistente (scan one-time) per non ripartire da 1
   * su reparti che hanno già compilazioni create da altri flussi.
   *
   * Ritorna `slotFull=true` (senza consumare l'indice) se supererebbe lo slot.
   */
  async reserveNextUtenteIndexByReparto(
    azienda: string,
    licenza: string,
    reparto: string,
    slotReparto: number = 0
  ): Promise<{ index: number; slotFull: boolean }> {
    const counterRef = this.db.firestore
      .collection('clienti').doc(azienda)
      .collection('licenze').doc(licenza)
      .collection('contatori').doc(reparto);

    // Seed: max corrente dai doc esistenti (one-time, fuori transaction)
    const scannedMax =
      (await this.getNextUtenteIndexByReparto(azienda, licenza, reparto)) - 1;

    return await this.db.firestore.runTransaction(async (tx) => {
      const snap = await tx.get(counterRef);
      const current = snap.exists ? Number(snap.data()?.['lastIndex'] ?? 0) : 0;
      const baseline = Math.max(current, scannedMax); // mai sotto al max reale
      const nextIndex = baseline + 1;

      if (slotReparto > 0 && nextIndex > slotReparto) {
        return { index: nextIndex, slotFull: true }; // nessuna scrittura
      }

      tx.set(counterRef, { lastIndex: nextIndex }, { merge: true });
      return { index: nextIndex, slotFull: false };
    });
  }


  getCompilazioniByRepartoField(
    azienda: string,
    licenza: string,
    reparto: string
  ) {
    return this.db
      .collection('clienti')
      .doc(azienda)
      .collection('licenze')
      .doc(licenza)
      .collection('compilazioni', ref =>
        ref.where('reparto', '==', reparto)
      )
      .valueChanges({ idField: 'id' }).pipe(takeUntil(this.ngUnsubscribe));
  }

  // ######################################
  // FUNCTION: getDocument
  getDocument(collection: string, id: string, collection2: string, id2: string): Observable<any> {
    return this.db.collection(collection).doc(id).collection(collection2).doc(id2).valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
  }

  // ######################################
  // FUNCTION: getSubDoc
  getSubDoc(collection: string, id: string, collection2: string, id2: string, collection3: string, id3: string) {
    return this.db
      .collection(collection)
      .doc(id)
      .collection(collection2)
      .doc(id2)
      .collection(collection3)
      .doc(id3)
      .valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
  }

  // ######################################
  // FUNCTION: getOnce
  getOnce(collection: string, id: string): Observable<any> {
    return this.db.collection(collection).doc(id).get().pipe(takeUntil(this.ngUnsubscribe));
  }

  // ######################################
  // FUNCTION: getDocOnce
  getDocOnce(collection: string, id: string, collection2: string, id2: string): Observable<any> {
    return this.db.collection(collection).doc(id).collection(collection2).doc(id2).get().pipe(takeUntil(this.ngUnsubscribe));
  }

  // ######################################
  // FUNCTION: getSubDocOnce
  getSubDocOnce(collection: string, id: string, collection2: string, id2: string, collection3: string, id3: string) {
    return this.db
      .collection(collection)
      .doc(id)
      .collection(collection2)
      .doc(id2)
      .collection(collection3)
      .doc(id3)
      .get().pipe(takeUntil(this.ngUnsubscribe));
  }

  // ######################################
  // FUNCTION: update
  update(collection: string, collection2: string, data: any, id: string, id2: string): Promise<void> {
    return this.db.collection(collection).doc(id).collection(collection2).doc(id2).update(data);
  }

  // ######################################
  // FUNCTION: updateSubDoc
  updateSubDoc(
    collection: string,
    collection2: string,
    collection3: string,
    data: any,
    id: string,
    id2: string,
    id3: string
  ): Promise<void> {
    return this.db
      .collection(collection)
      .doc(id)
      .collection(collection2)
      .doc(id2)
      .collection(collection3)
      .doc(id3)
      .update(data);
  }

  // ######################################
  // FUNCTION: addMultipleSubDoc
  // PERF: usa WriteBatch — un unico round-trip invece di N scritture sequenziali
  addMultipleSubDoc(
    collection: string,
    collection2: string,
    collection3: string,
    id: string,
    id2: string,
    data: any[]
  ): Promise<void> {
    const batch = this.db.firestore.batch();
    for (const item of data) {
      const ref = this.db.firestore
        .collection(collection).doc(id)
        .collection(collection2).doc(id2)
        .collection(collection3).doc(item.id);
      batch.set(ref, Object.assign({}, item));
    }
    return batch.commit();
  }

  async addSingleSubDoc(
    collection: string,
    collection2: string,
    collection3: string,
    id: string,
    id2: string,
    data: any
  ): Promise<void> {
    return await this.db
      .collection(collection)
      .doc(id)
      .collection(collection2)
      .doc(id2)
      .collection(collection3)
      .doc(data.id)
      .set(Object.assign({}, data));

  }

  async addSingleSubDocCheck(
    collection: string,
    collection2: string,
    collection3: string,
    id: string,
    id2: string,
    data: any
  ): Promise<Boolean> {
    try {
      await this.db
        .collection(collection)
        .doc(id)
        .collection(collection2)
        .doc(id2)
        .collection(collection3)
        .doc(data.id)
        .set(Object.assign({}, data));

      return true;
    } catch (error) {
      console.error(`Failed to write document: ${error}`);
      return false;
    }
  }



  // ######################################
  // FUNCTION: updateCollection
  updateCollection(collection: string, data: any, id: string): Promise<void> {
    return this.db.collection(collection).doc(id).update(data);
  }

  // ######################################
  // FUNCTION: delete
  delete(collection: string, collection2: string, id: string, id2: string): Promise<void> {
    return this.db.collection(collection).doc(id).collection(collection2).doc(id2).delete();
  }


  // ######################################
  // FUNCTION: deleteDoc
  deleteDoc(collection: string, id): Promise<void> {
    return this.db.collection(collection).doc(id).delete();
  }

  // ######################################
  // FUNCTION: deleteSubDoc
  deleteSubDoc(
    collection: string,
    collection2: string,
    collection3: string,
    id: string,
    id2: string,
    id3: string
  ): Promise<void> {
    return this.db
      .collection(collection)
      .doc(id)
      .collection(collection2)
      .doc(id2)
      .collection(collection3)
      .doc(id3)
      .delete();
  }

  // ######################################
  // FUNCTION: findLicenzeByQuest
  // Cerca tutte le licenze (in qualunque cliente) che usano questa versione del questionario.
  // Usa collectionGroup per evitare la cascata getAll(clienti) → getCollection(licenze).
  async findLicenzeByQuest(qId: string, vId: string): Promise<any[]> {
    try {
      const snap = await this.db.firestore
        .collectionGroup("licenze")
        .where("questionario", "==", qId)
        .where("versione", "==", vId)
        .get();

      return snap.docs.map((d) => {
        const parent = d.ref.parent.parent; // clienti/{clienteId}
        return {
          id: d.id,
          cliente: parent ? parent.id : "",
          path: d.ref.path,
          ...(d.data() as any),
        };
      });
    } catch (err) {
      console.debug("findLicenzeByQuest error:", err);
      return [];
    }
  }

  // ######################################
  // FUNCTION: getLast
  getLast(collection: string, field: string): Promise<number> {
    return new Promise((promise) => {
      this.db.firestore
        .collection(collection)
        .orderBy(field, "desc")
        .limit(1)
        .get()
        .then((querySnapshot) => {
          if (querySnapshot.empty) {
            return promise(1);
          } else {
            return promise(parseInt(querySnapshot.docs[0].data().codice) + 1);
          }
        });
    });
  }

  // ######################################
  // FUNCTION: getLastBy
  getLastBy(collection: string, field: string): Promise<number> {
    return new Promise((promise) => {
      this.db.firestore
        .collection(collection)
        .orderBy(field, "desc")
        .limit(1)
        .get()
        .then((querySnapshot) => {
          if (querySnapshot.empty) {
            return promise(0);
          } else {
            return promise(parseInt(querySnapshot.docs[0].data().codice));
          }
        });
    });
  }

  // ######################################
  // FUNCTION: getLastSubBy
  getLastSubBy(collection: string, id: string, collection2: string, field: string): Promise<number> {
    return new Promise((promise) => {
      this.db.firestore
        .collection(collection)
        .doc(id)
        .collection(collection2)
        .orderBy(field, "desc")
        .limit(1)
        .get()
        .then((querySnapshot) => {
          if (querySnapshot.empty) {
            return promise(0);
          } else {
            return promise(parseInt(querySnapshot.docs[0].data().codice));
          }
        });
    });
  }

  // ######################################
  // FUNCTION: getLastSubSubBy
  getLastSubSubBy(
    collection: string,
    id: string,
    collection2: string,
    id2: string,
    collection3: string,
    field: string
  ): Promise<number> {
    return new Promise((promise) => {
      this.db.firestore
        .collection(collection)
        .doc(id)
        .collection(collection2)
        .doc(id2)
        .collection(collection3)
        .orderBy(field, "desc")
        .limit(1)
        .get()
        .then((querySnapshot) => {
          if (querySnapshot.empty) {
            return promise(0);
          } else {
            return promise(parseInt(querySnapshot.docs[0].data().codice));
          }
        });
    });
  }

  // ######################################
  // FUNCTION: getAll
  getAll(collection: string, order: string = ""): Observable<any> {
    if (order === "") {
      return this.db.collection(collection).valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
    } else {
      return this.db.collection(collection, (ref) => ref.orderBy(order, "desc")).valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
    }
  }

  // ######################################
  // FUNCTION: getAllAsc
  getAllAsc(collection: string, order: string = ""): Observable<any> {
    if (order === "") {
      return this.db.collection(collection).valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
    } else {
      return this.db.collection(collection, (ref) => ref.orderBy(order, "asc")).valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
    }
  }

  // ######################################
  // FUNCTION: getCollectionByOrder
  getCollectionByOrder(collection: string, id: string, collection2: string, order: string = ""): Observable<any> {
    if (order === "") {
      return this.db.collection(collection).doc(id).collection(collection2).valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
    } else {
      return this.db
        .collection(collection)
        .doc(id)
        .collection(collection2, (ref) => ref.orderBy(order, "desc"))
        .valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
    }
  }

  // ######################################
  // FUNCTION: getByFilter
  getByFilter(collection: string, field: string = "", filterValue: string = "", order: string = ""): Observable<any> {
    if (field === "" || order === "") {
      return this.db.collection(collection).valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
    } else {
      return this.db
        .collection(collection, (ref) => ref.where(field, "==", filterValue).orderBy(order, "desc"))
        .valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
    }
  }

  // ######################################
  // FUNCTION: getByDoubleFilter
  getByDoubleFilter(
    collection: string,
    field: string = "",
    filterValue: string = "",
    order: string = "",
    field2: string = "",
    filterValue2: string = ""
  ): Observable<any> {
    if (field === "" || order === "") {
      return this.db.collection(collection).valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
    } else {
      return this.db
        .collection(collection, (ref) =>
          ref.where(field, "==", filterValue).where(field2, "==", filterValue2).orderBy(order, "desc")
        )
        .valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
    }
  }

  // ######################################
  // FUNCTION: getAllAscOnce — lettura one-time (no listener) ordinata asc
  async getAllAscOnce(collection: string, order: string = ""): Promise<any[]> {
    try {
      let ref: any = this.db.firestore.collection(collection);
      if (order !== "") {
        ref = ref.orderBy(order, "asc");
      }
      const snap = await ref.get();
      return snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.debug("getAllAscOnce error:", err);
      return [];
    }
  }

  // ######################################
  // FUNCTION: getCollectionOnce — lettura one-time di una subcollection
  async getCollectionOnce(collection: string, id: string, collection2: string): Promise<any[]> {
    try {
      const snap = await this.db.firestore
        .collection(collection)
        .doc(id)
        .collection(collection2)
        .get();
      return snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.debug("getCollectionOnce error:", err);
      return [];
    }
  }

  // ######################################
  // FUNCTION: getByDoubleFilterOnce — lettura one-time con doppio where
  async getByDoubleFilterOnce(
    collection: string,
    field: string = "",
    filterValue: string = "",
    order: string = "",
    field2: string = "",
    filterValue2: string = ""
  ): Promise<any[]> {
    try {
      let ref: any = this.db.firestore.collection(collection);
      if (field === "" || order === "") {
        const snap = await ref.get();
        return snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
      }
      const snap = await ref
        .where(field, "==", filterValue)
        .where(field2, "==", filterValue2)
        .orderBy(order, "desc")
        .get();
      return snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.debug("getByDoubleFilterOnce error:", err);
      return [];
    }
  }

  // ######################################
  // FUNCTION: getSubByFilter
  getSubByFilter(
    collection: string,
    doc: string,
    collection2,
    field: string = "",
    filterValue: string = "",
    field2: string = "",
    filterValue2: string = ""
  ): Observable<any> {
    if (field === "") {
      return this.db.collection(collection).valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
    } else {
      if (field2 === "") {
        return this.db
          .collection(collection)
          .doc(doc)
          .collection(collection2, (ref) => ref.where(field, "==", filterValue))
          .valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
      } else {
        return this.db
          .collection(collection)
          .doc(doc)
          .collection(collection2, (ref) => ref.where(field, "==", filterValue).where(field2, "==", filterValue2))
          .valueChanges({ idField: "id" }).pipe(takeUntil(this.ngUnsubscribe));
      }
    }
  }

  // ######################################
  // FUNCTION: getSettingOptions
  getSettingOptions(): Observable<Impostazioni[] | undefined> {
    return this.db.collection<Impostazioni>('impostazioni').valueChanges().pipe(takeUntil(this.ngUnsubscribe));
  }

  getAllUnsubscribed() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    // Reset: dopo complete() il Subject non emetterebbe più nulla.
    // Le subscription future con takeUntil(ngUnsubscribe) funzionano correttamente.
    this.ngUnsubscribe = new Subject<void>();
  }
}
